import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Trophy } from 'lucide-react';
import { Ranking, RankingPageListing, RecentRankingItem, formatRankingDate, rankingLastUpdated, sortRankingEntries } from '../../services/rankingService';
import infoPageService from '../../services/infoPageService';
import { t } from '../../i18n';

// ============================================
// Ranglisten-Vorschaukarte (Marcel 22.10.2026: „auf der Seite einer Liga sollen die
// Ranglisten auch so aussehen wie auf der Startseite“): EINE Karte für die Startseite
// („Aktuelle Ranglisten“) und den Ranglisten-Block der Listing-Seiten – Logo, Listing-Name,
// Typ, Titel · Saison, Top 3 mit Punkten, „N Spieler · Aktualisiert dd.mm.yyyy“. Echter Link.
// ============================================

const ACCENT = '#3fced1';
export const PREVIEW_CARD_WIDTH = 240;

export const rankingTypeLabel = (type: string): string => {
  switch (type) {
    case 'club': return t('web.component.home-recent-rankings.typ-club');
    case 'league': return t('web.component.home-recent-rankings.typ-league');
    case 'association': return t('web.component.home-recent-rankings.typ-association');
    case 'pub': return t('web.component.home-recent-rankings.typ-pub');
    default: return type;
  }
};

/**
 * PURE: Rangliste eines Listings (GET /api/rankings/:type/:id?top=N) → Karten-Daten wie auf der Startseite
 * (Top 3 nach Rang, Gesamtzahl aus entryCount, Stand aus updatedAt/lastImportAt). Ohne Pfad: path = '' (Karte ohne Link).
 */
export const rankingToPreviewItem = (r: Ranking, listing: RankingPageListing): RecentRankingItem | null => {
  if (!r?.id || !r.title) return null;
  const entries = sortRankingEntries(r.entries ?? [], r.sortDirection ?? 'desc');
  const count = typeof r.entryCount === 'number' && r.entryCount > 0 ? r.entryCount : entries.length;
  return {
    id: r.id,
    slug: r.slug ?? '',
    path: r.path ?? '',
    title: r.title,
    season: r.season ?? null,
    entryCount: count,
    lastUpdated: rankingLastUpdated(r) ?? 0,
    listing,
    top: entries.slice(0, 3).map((e) => ({ rank: e.rank ?? null, playerName: e.playerName, points: typeof e.points === 'number' ? e.points : null })),
  };
};

const RankingPreviewCard: React.FC<{ item: RecentRankingItem; testId?: string; className?: string }> = ({ item, testId = 'ranking-preview-card', className = '' }) => {
  const [imgError, setImgError] = useState(false);
  const image = infoPageService.buildFileUrl(item.listing.image);
  const showImage = image && !imgError;
  const cls = `group flex shrink-0 flex-col overflow-hidden rounded-2xl border border-zinc-800 bg-zinc-900 text-left transition-colors hover:border-[#3fced1]/50 ${className}`;
  const body = (
    <>
      <div className="flex items-center gap-2.5 px-3 pt-3">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-zinc-800">
          {showImage ? (
            <img src={image as string} alt="" loading="lazy" onError={() => setImgError(true)} className="h-full w-full object-cover" />
          ) : (
            <Trophy size={16} style={{ color: ACCENT }} />
          )}
        </div>
        <div className="min-w-0">
          <span className="block truncate text-[12px] font-semibold text-gray-300">{item.listing.name || rankingTypeLabel(item.listing.type)}</span>
          <span className="block text-[10.5px] font-bold uppercase tracking-wide" style={{ color: ACCENT }}>{rankingTypeLabel(item.listing.type)}</span>
        </div>
      </div>
      <div className="flex flex-1 flex-col px-3 pb-3 pt-2.5">
        <h3 className="line-clamp-2 text-[13px] font-semibold leading-snug text-white transition-colors group-hover:text-[#3fced1]">
          {item.title}{item.season ? ` · ${item.season}` : ''}
        </h3>
        {item.top.length > 0 && (
          <ol className="mt-2 space-y-0.5">
            {item.top.map((e) => (
              <li key={`${e.rank}-${e.playerName}`} className="flex items-center gap-2 text-[11.5px] text-gray-400">
                <span className="w-4 shrink-0 font-bold text-gray-500">{e.rank ?? '–'}.</span>
                <span className="min-w-0 flex-1 truncate text-gray-300">{e.playerName}</span>
                {e.points !== null && <span className="shrink-0 font-semibold" style={{ color: ACCENT }}>{e.points}</span>}
              </li>
            ))}
          </ol>
        )}
        <p className="mt-auto pt-2 text-[11px] text-gray-500">
          {t('web.component.home-recent-rankings.spieler', { count: item.entryCount })}
          {item.lastUpdated > 0 ? ` · ${t('web.component.home-recent-rankings.aktualisiert', { date: formatRankingDate(item.lastUpdated) })}` : ''}
        </p>
      </div>
    </>
  );
  // Ohne Pfad (z. B. Rangliste ohne Slug) keine tote Verlinkung – gleiche Karte als <span>
  return item.path
    ? <Link to={item.path} data-testid={testId} style={{ width: PREVIEW_CARD_WIDTH }} className={cls}>{body}</Link>
    : <span data-testid={testId} style={{ width: PREVIEW_CARD_WIDTH }} className={cls}>{body}</span>;
};

export default RankingPreviewCard;
